'use client';

import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';

import {
  Button,
  type ButtonMode,
  type DefaultIconSize,
  type IconShapeVariant,
} from '@repo/ui/Button';
import { Icon } from '@repo/ui/Icon';
import { type IconNames } from '@repo/ui/Icon/utils';
import { VisuallyHidden } from '@repo/ui/VisuallyHidden';

export interface IconButtonProps extends ComponentPropsWithRef<'button'> {
  ariaDisabled?: boolean;
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  icon: IconNames;
  isLoading?: boolean;
  label: string | ReactNode;
  loadingLabel?: string;
  mode?: ButtonMode;
  size?: DefaultIconSize;
  variant?: IconShapeVariant;
}

export const IconButton = (props: IconButtonProps) => {
  const {
    asChild = false,
    children,
    disabled = false,
    icon,
    isLoading = false,
    label,
    size = 'md',
    variant = 'primary',
    ...attrs
  } = props;

  const iconContent = (
    <>
      <Icon name={icon} size="sm" />
      <VisuallyHidden>{label}</VisuallyHidden>
    </>
  );

  let buttonChildren: ReactNode = iconContent;

  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement<{ children?: ReactNode }>(child)) {
      throw new Error(
        'IconButton: child must be a valid React element when asChild is true',
      );
    }
    buttonChildren = cloneElement(child, undefined, iconContent);
  }

  return (
    <Button
      asChild={asChild}
      disabled={disabled}
      isLoading={isLoading}
      shape="icon"
      size={size}
      variant={variant}
      {...attrs}
    >
      {buttonChildren}
    </Button>
  );
};
